import { useEffect, useState } from "react";
import {
  Outlet,
  NavLink,
  useParams,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import {
  FiLayers,
  FiUsers,
  FiArrowLeft,
  FiPlus,
  FiSearch,
} from "react-icons/fi";
import styles from "./styles/ProjectLayout.module.css";
import CreateSegments from "Components/FeatureFlip/Segments/CreateSegments/CreateSegments";
import CreateFeature from "Components/FeatureFlip/Features/CreateFeature/CreateFeature";
import { useGetAllContexts } from "API/Queries/useGetAllContexts";
import { useFeatureFlipStore } from "Store/featureFlipStore";
import Loader from "Components/Loader";
import CreateContextModal from "Components/FeatureFlip/Common/Context/CreateContextModal";
import ContextBottomSheet from "Components/FeatureFlip/Common/Context/ContextBottomSheet";
import { ROUTES } from "Utils/routes";
import { isInFeatureOrSegmentPage } from "Utils/utils";

const ProjectLayout = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isFeatures = location.pathname === ROUTES.PROJECT_FEATURES(id!);
  const isSegments = location.pathname === ROUTES.PROJECT_SEGMENTS(id!);
  const isFeaturesOrSegments = isFeatures || isSegments;

  const { mutate: getAllContexts, isLoading: isContextLoading } =
    useGetAllContexts();

  const [showSegmentForm, setShowSegmentForm] = useState(false);
  const [showFeatureForm, setShowFeatureForm] = useState(false);
  const [showContextModal, setShowContextModal] = useState(false);
  const [showContextSheet, setShowContextSheet] = useState(false);

  const setContexts = useFeatureFlipStore((s) => s.setContexts);
  const searchTerm = useFeatureFlipStore((s) => s.searchTerm);
  const setSelectedProject = useFeatureFlipStore((s) => s.setSelectedProject);
  const setSearchTerm = useFeatureFlipStore((s) => s.setSearchTerm);

  useEffect(() => {
    if (!isInFeatureOrSegmentPage(location.pathname)) return;

    const handlePopState = () => {
      navigate(ROUTES.FEATURE_FLIP, { replace: true });
    };

    window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", handlePopState);
    setSelectedProject(`${id}`);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [location.pathname]);

  const handleBack = () => {
    if (isInFeatureOrSegmentPage(location.pathname)) {
      navigate(ROUTES.FEATURE_FLIP);
    } else {
      navigate(-1);
    }
  };

  const handleCreateNewSegments = () => {
    getAllContexts(`${id}`, {
      onSuccess: (data) => {
        setContexts(data);
        setShowSegmentForm(true);
      },
      onError: (error: unknown) => {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to create project";
      },
    });
  };

  const handleCreateNewFeatures = () => {
    getAllContexts(`${id}`, {
      onSuccess: (data) => {
        setContexts(data);
        setShowFeatureForm(true);
      },
      onError: (error: unknown) => {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to create project";
      },
    });
  };

  const handleContextButtonClick = () => {
    getAllContexts(`${id}`, {
      onSuccess: (data) => {
        setContexts(data);
        setShowContextSheet(true);
      },
    });
  };

  const handleCloseContextSheet = () => {
    setShowContextSheet(false);
  };

  const handleCreateNewContext = () => {
    getAllContexts(`${id}`);
    setShowContextSheet(false);
    setShowContextModal(true);
  };

  const handleContextModalClose = () => {
    setShowContextModal(false);
    setShowContextSheet(true);
  };

  const handleContextModalSuccess = () => {
    setShowContextModal(false);
    setShowContextSheet(true);
  };

  const handleAddButtonClick = () => {
    if (isSegments) {
      handleCreateNewSegments();
    } else {
      handleCreateNewFeatures();
    }
  };

  if (location.pathname === ROUTES.PROJECT(id!)) {
    return <Navigate to={ROUTES.PROJECT_FEATURES(id!)} replace />;
  }

  return (
    <div className={styles.container}>
      {isContextLoading && <Loader />}
      <div className={styles.header}>
        <div>
          <button className={styles.backButton} onClick={handleBack}>
            <FiArrowLeft /> Back
          </button>
          <div className={styles.tabs}>
            <NavLink
              to={ROUTES.PROJECT_FEATURES(id!)}
              className={({ isActive }) =>
                `${styles.tab} ${isActive ? styles.active : ""}`
              }
            >
              <FiLayers />
              Features
            </NavLink>
            <NavLink
              to={ROUTES.PROJECT_SEGMENTS(id!)}
              className={({ isActive }) =>
                `${styles.tab} ${isActive ? styles.active : ""}`
              }
            >
              <FiUsers />
              Segments
            </NavLink>
          </div>
        </div>
        {isFeaturesOrSegments && (
          <div className={styles.actions}>
            <div className={styles.searchBox}>
              <FiSearch className={styles.searchIcon} />
              <input
                type="text"
                placeholder={`Search ${isFeatures ? "features" : "segments"}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={styles.searchInput}
              />
            </div>
            <div className={styles.buttonGroup}>
              <button
                onClick={handleContextButtonClick}
                className={styles.contextButton}
              >
                <FiLayers />
                Contexts
              </button>
              <button
                onClick={handleAddButtonClick}
                className={styles.addButton}
              >
                <FiPlus /> {isFeatures ? "New Feature" : "New Segment"}
              </button>
            </div>
          </div>
        )}
      </div>

      <div className={styles.content}>
        <Outlet />
      </div>

      {showSegmentForm && (
        <CreateSegments setShowAddForm={setShowSegmentForm} />
      )}
      {showFeatureForm && (
        <CreateFeature setShowFeatureForm={setShowFeatureForm} />
      )}

      <CreateContextModal
        isOpen={showContextModal}
        onClose={handleContextModalClose}
        onSuccess={handleContextModalSuccess}
      />

      <ContextBottomSheet
        isOpen={showContextSheet}
        onClose={handleCloseContextSheet}
        onCreateNew={handleCreateNewContext}
      />
    </div>
  );
};

export default ProjectLayout;
